import React, { useEffect, useMemo, useRef, useState } from "react";
import Button from "../Button/Button";
import {
  ArrowIcon,
  CheckBoxActiveIcon,
  CheckBoxInActiveIcon,
  CloseIcon,
  SearchIcon
} from "../../Assets/assets";
import { isValidArray } from "../../Services/validators";
import { search } from "fast-fuzzy";
import "./Filters.css";
import ScrollBar from "../ScrollBar/ScrollBar";

export default function MultipleSelect(props) {
  const dropdownRef = useRef(null);
  const [show, setShow] = useState(false);
  const [searchKey, setSearchKey] = useState("");

  const searchedData = useMemo(() => {
    if (!searchKey) {
      return props.data?.options;
    }
    return search(searchKey, props.data?.options, {
      keySelector: (item) => item,
      threshold: 0.7
    }).sort((a, b) => a.localeCompare(b));
  }, [searchKey, props.data?.options]);

  const handleSelect = (data) => {
    if (typeof data === "string" && data?.toLowerCase() === "select all") {
      if (props.selectedFilters?.[props.data?.key]?.includes(data)) {
        props.setSelectedFilters({
          ...props.selectedFilters,
          [props.data?.key]: props.defaultFilters?.[props.data?.key]
        });
      } else {
        return props.setSelectedFilters({
          ...props.selectedFilters,
          [props.data?.key]: props.data?.options
        });
      }
    } else if (props.selectedFilters?.[props.data?.key]?.includes(data)) {
      props.setSelectedFilters({
        ...props.selectedFilters,
        [props.data?.key]: props.selectedFilters?.[props.data?.key].filter(
          (value) => data !== value
        )
      });
    } else {
      props.setSelectedFilters({
        ...props.selectedFilters,
        [props.data?.key]: [
          ...(isValidArray(props.selectedFilters?.[props.data?.key])
            ? props.selectedFilters?.[props.data?.key]
            : []),
          data
        ]
      });
    }
  };

  const getFilterSelected = () => {
    if (!props.selected?.length) {
      return "All";
    }

    if (props.selected.length === 1) {
      return isValidArray(props.selected[0])
        ? props.selected[0]
        : props.selected;
    }

    const firstSelected = isValidArray(props.selected)
      ? props.selected[0]
      : props.selected;
    const additionalCount = props.data?.multiple
      ? `+ ${props.selected.length - 1}`
      : "";
    return `${
      firstSelected.length > 20
        ? firstSelected.slice(0, 20) + "..."
        : firstSelected
    } ${additionalCount}`;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShow(false);
      }
    };

    const handleEscKey = (event) => {
      if (event.key === "Escape") {
        setShow(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscKey);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscKey);
    };
    // eslint-disable-next-line
  }, []);

  const isFilterApplied = useMemo(() => {
    const applied = props.appliedFilters?.[props.data?.key] || [];
    if (applied.length > 0) {
      return true;
    } else {
      return false;
    }
    // eslint-disable-next-line
  }, [props.appliedFilters]);

  const isSelectedNotApplied = useMemo(() => {
    if (
      props.selectedFilters?.[props.data?.key]?.length ===
      props.appliedFilters?.[props.data?.key]?.length
    ) {
      return false;
    } else {
      return true;
    }
  }, [props.selectedFilters, props.appliedFilters, props.data?.key]);

  return (
    <div
      data-cy={`${props?.["data-cy"]}-SELECT-FILTER`}
      ref={dropdownRef}
      className={`${props.disabled ? "cursor-no-drop" : "cursor-pointer"}`}
    >
      <div
        onClick={() => {
          if (!props.disabled) {
            setShow(!show);
          }
        }}
        style={{
          ...(show || isFilterApplied
            ? {
                backgroundColor: "var(--tertiaryColor8)",
                border: "solid 1px var(--tertiaryColor5)"
              }
            : { border: "solid 1px transparent" }),
          ...(!show && isSelectedNotApplied
            ? { border: "solid 1px var(--tertiaryColor5)" }
            : {})
        }}
        className={`display-flex flex-align-items-center padding-default position-relative`}
      >
        {!show && isSelectedNotApplied && (
          <div
            style={{
              top: "-2px",
              right: "-2px",
              borderRadius: "50%",
              width: "7px",
              height: "7px",
              backgroundColor: "#1c1c1c",
              zIndex: 100
            }}
            className="position-absolute flex-justify-content-center flex-align-items-center"
          >
            <div
              style={{
                backgroundColor: "var(--secondaryColor)",
                width: "6px",
                height: "6px",
                borderRadius: "50%"
              }}
            />
          </div>
        )}
        <div className="font-color-tertiaryColor6 font-size-small white-space-nowrap">
          {props.data?.title}
        </div>
        <div className="font-size-medium font-color-tertiaryColor padding-left-small white-space-nowrap padding-right-small">
          {getFilterSelected()}
        </div>
        <div
          style={{
            height: "16px",
            width: "16px"
          }}
        >
          <ArrowIcon color="var(--tertiaryColor6)" />
        </div>
      </div>

      {show && props.data?.options && (
        <div
          style={{
            height: "200px",
            minWidth: "250px",
            maxWidth: "250px",
            top: "48px",
            zIndex: 2000
          }}
          className="position-absolute padding-large box-shadow-default background-color-tertiaryColor2 font-color-tertiaryColor"
        >
          <div
            style={{ height: "calc(100% - 48px)" }}
            className="inherit-parent-height inherit-parent-width"
          >
            {props.data?.search && (
              <div>
                <SearchOptions
                  label={props.data?.searchData.label}
                  onChange={(e) => {
                    setSearchKey(e.target.value);
                  }}
                  onCloseClick={() => {
                    setSearchKey("");
                  }}
                  searchKey={searchKey}
                />
              </div>
            )}
            <ScrollBar
              height={`calc(100% - ${props.data?.search ? "42" : "0"}px)`}
            >
              {searchedData?.map((data, i) => (
                <div
                  data-cy={`${data?.toString().toUpperCase()}-${
                    props?.["data-cy"]
                  }-SELECT-FILTER-OPTION`}
                  key={i}
                  className={`display-flex ${
                    i === 0 ? "" : "padding-top-default"
                  } flex-align-items-center`}
                  onClick={() => {
                    handleSelect(data);
                  }}
                  title={data}
                >
                  <div
                    style={{
                      height: "16px",
                      width: "16px"
                    }}
                    className="margin-right-default display-flex flex-align-items-center"
                  >
                    {props.selectedFilters?.[props.data?.key]?.includes(
                      data
                    ) ? (
                      typeof data === "string" &&
                      props.appliedFilters?.[props.data?.key].includes(data) ? (
                        <CheckBoxActiveIcon color="var(--secondaryColor)" />
                      ) : (
                        <CheckBoxActiveIcon color="var(--tertiaryColor6)" />
                      )
                    ) : (
                      <CheckBoxInActiveIcon color="var(--tertiaryColor6)" />
                    )}
                  </div>
                  <div
                    style={{
                      width: "200px"
                    }}
                    className="white-space-nowrap text-overflow-ellipsis"
                  >
                    {data}
                  </div>
                </div>
              ))}
            </ScrollBar>
          </div>
          <div
            className={`display-flex margin-top-large ${
              props.data?.options.length < 6 ? "" : "margin-bottom-large"
            } flex-justify-content-space-between`}
          >
            <div className="inherit-parent-width">
              <Button
                data-cy={`${props?.["data-cy"]}-SELECT-RESET-BUTTON`}
                variant="danger"
                text="Reset"
                boxShadow={false}
                className="inherit-parent-width padding-left-large padding-right-large"
                onClick={() => {
                  props.onResetClick(props.data?.key);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const SearchOptions = (props) => {
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
      }}
      style={{ height: 36 }}
      className={`inherit-parent-width margin-bottom-default`}
    >
      <div className="display-flex flex-justify-content-center flex-align-items-center">
        <input
          style={{
            width: "190px"
          }}
          className="font-color-tertiaryColor background-color-tertiaryColor2 inherit-parent-height margin-bottom-default border-none font-size-medium inherit-parent-width"
          value={props.searchKey}
          onChange={props.onChange}
          title={props.label}
          placeholder={props.label}
          data-cy="search-input-field"
        />

        <button
          className="padding-right-default background-color-transparent cursor-pointer"
          type="button"
          onClick={() => {
            if (props.searchKey) {
              props.onCloseClick();
            }
          }}
        >
          {!props.searchLoading && !props.searchKey ? (
            <SearchIcon color="var(--tertiaryColor6)" />
          ) : (
            <></>
          )}

          {!props.searchLoading && props.searchKey ? (
            <CloseIcon color="var(--tertiaryColor6)" width={16} height={16} />
          ) : (
            <></>
          )}

          {props.searchLoading ? (
            <div
              className="export-loader"
              style={{ height: "16px", width: "16px" }}
            />
          ) : (
            <></>
          )}
        </button>
      </div>
      <div
        style={{
          height: "2px",
          backgroundColor: props.searchKey ? "#FFFFFF" : "#3E3E3E"
        }}
        className="inherit-parent-width"
      />
    </form>
  );
};
